import React, { PropsWithChildren, ReactElement } from 'react'
import { Flex, Text, ThemeUIStyleObject } from 'theme-ui'
import { Svg } from 'ui'
import CheckIcon from 'images/checkmark-green.svg'
import CancelIcon from 'images/cross-status.svg'
import LoadingPlaceholder from './LoadingPlaceholder'
import { TextWithBreakpointVariant } from 'ui/TextWithBreakpointVariant'

export default function Group({
  title,
  badgeText,
  isSuccess,
  children,
  sxMainContent,
  isLoading,
}: PropsWithChildren<{
  title?: string
  badgeText?: string
  isSuccess?: boolean
  sxMainContent?: ThemeUIStyleObject
  isLoading?: boolean
}>): ReactElement {
  return (
    <Flex
      sx={{
        width: '100%',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      {title && (
        <Flex
          sx={{
            width: '100%',
            pl: ['2rem', '2.4rem'],
            pr: ['1.2rem', '2.4rem'],
            py: '1.2rem',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderRadius: '1.2rem',
            background:
              'linear-gradient(180deg, rgba(118, 219, 255, 0.10) 0%, rgba(118, 219, 255, 0.00) 100%)',
            boxShadow:
              '0px 0px 0px 0px rgba(118, 219, 255, 0.00),0px 1px 0.2px 0px rgba(118, 219, 255, 0.10) inset',
          }}
        >
          <Text
            variant="size15Weight700Line138"
            sx={{
              color: 'aliceBlue',
            }}
          >
            {title}
          </Text>
          <LoadingPlaceholder isLoading={isLoading} sx={{ mr: '0.6rem' }}>
            {isSuccess !== undefined && <StatusBadge text={badgeText} isSuccess={isSuccess} />}
          </LoadingPlaceholder>
        </Flex>
      )}
      <Flex
        sx={{
          width: '100%',
          border: '1px solid',
          borderColor: 'lightSkyBlue10',
          borderRadius: '1.2rem',
          p: '0.8rem',
          flexDirection: 'column',
          color: 'lightSteelBlue',
          ...sxMainContent,
        }}
      >
        {children}
      </Flex>
    </Flex>
  )
}

export function GroupItem({
  children,
  sx,
}: PropsWithChildren<{ sx?: ThemeUIStyleObject }>): ReactElement {
  return (
    <Flex
      sx={{
        py: '1.2rem',
        px: ['0.8rem', '2.4rem'],
        justifyContent: 'space-between',
        alignItems: 'center',
        ...sx,
      }}
    >
      {children}
    </Flex>
  )
}

function StatusBadge({ text, isSuccess }: { text?: string; isSuccess?: boolean }) {
  return (
    <Flex
      sx={{
        pl: '1.2rem',
        pr: '0.6rem',
        py: '0.6rem',
        borderRadius: '10rem',
        backgroundColor: 'blue',
        gap: '1.2rem',
        alignItems: 'center',
        borderBottom: '1px solid',
        borderBottomColor: 'lightSkyBlue10',
      }}
    >
      <TextWithBreakpointVariant
        variant={['size12Weight400Line138', 'size15Weight400Line138']}
        sx={{ color: isSuccess ? 'successOpaque' : 'errorOpaque' }}
      >
        {text}
      </TextWithBreakpointVariant>
      <Svg
        svg={isSuccess ? CheckIcon : CancelIcon}
        descriptiveText={isSuccess ? 'Success status' : 'Error status'}
        sx={{ width: '2.4rem', height: '2.4rem' }}
      />
    </Flex>
  )
}
